import { ErrorFilled } from '@carbon/icons-react';
import { ProgressBar } from 'components/progress-bar/progress-bar';
import { StatusChecklist } from 'components/workflow-status-popover/status-checklist';
import { runProgressHeadline, toStatusItems } from '../../utils';
import type { RunProgressStepResponse } from '../../types';
import styles from './run-progress.module.scss';

interface RunProgressProps {
  /** The run's stages, in run order (the BE `RunProgress` rows). */
  progress: RunProgressStepResponse[];
}

/**
 * A comparison run's stages as a panel, in the app's step-progress pattern — the UI counterpart
 * of the job framework the stages are shaped after: a headline bar (where the run stopped, how
 * far it got) over the `StatusChecklist`, one row per stage with its status icon and any
 * failure / not-run reason. Shown under a failed run's notice, so the stopped stage stays visible
 * (a live run shows just its status icon in the results toolbar, which opens the same stages).
 *
 * @param {RunProgressProps} props - The run's stages.
 * @return {JSX.Element} The progress panel.
 */
export function RunProgress({ progress }: RunProgressProps) {
  const { statusLabel, percent } = runProgressHeadline(progress);
  return (
    <section className={styles.panel} aria-label="Comparison stages">
      <ProgressBar
        value={percent}
        statusLabel={statusLabel}
        statusIcon={<ErrorFilled className={styles.stoppedIcon} />}
      />
      <div className={styles.checklist}>
        <StatusChecklist statusItems={toStatusItems(progress)} showStatusIcon />
      </div>
    </section>
  );
}
